/**
 * 이모 수첩 적기 시트 (시안 13 N2 · N3 · N3b · N5, SPEC_CHANGE_v1.3 25.1 · C-31 · D75).
 *
 *   손잡이 → 이모 초상 32 + 「수첩에 적어 둘 것」 → 입력칸(60자, 넘으면 더 안 받음, 숫자 카운터 없음)
 *   → 누가 알게 할까요(모두 (별채) + 멤버 다섯, 하나만. 1:1 에서 열면 그 멤버로 고정 + 안내)
 *   → 어떤 얘기예요 (선택): 사람 · 일 · 취향 · 일정(일정이면 날짜 + 「그날 멤버가 먼저 물어봐요.」)
 *   → 민감 안내(서버가 NOTE_SENSITIVE 로 알려 주면 주황 판, 한 번 더 누르면 적음) → 「수첩에 적기」
 * 위기(L2 이상) 표현이면 적지 않고 안전 카드(CRISIS_NOTE). 저장하면 토스트 없이 닫힙니다.
 * 단골이 적은 줄을 고칠 때도 같은 시트(editing): 문장 · 누가 알게 할지 · 분류 · 날짜.
 */
import { useEffect, useRef, useState } from 'react';
import { MEMBER_KEYS, getMember } from '@config/members/index.js';
import * as meApi from '../../api/me.js';
import { errorMessageOf } from '../../api/client.js';
import { track } from '../../api/track.js';
import { useNoteSheet } from '../../stores/noteSheet.js';
import { useMemoBook } from '../../stores/memo.js';
import { Avatar } from '../Avatar.jsx';
import { BottomSheet } from '../BottomSheet.jsx';
import { Button } from '../Button.jsx';
import { SafetyCard } from '../SafetyCard.jsx';

export const NOTE_MAX_LENGTH = 60;
export const NOTE_KIND_LABELS = { person: '사람', work: '일', taste: '취향', schedule: '일정' };

function todayInSeoul() {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
}

/** 60자까지만(자를 때 글자 단위) */
export function clipNote(text) {
    return Array.from(String(text ?? ''))
        .slice(0, NOTE_MAX_LENGTH)
        .join('');
}

function ChoiceChip({ selected, onClick, children, disabled = false, height = 36 }) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={onClick}
            style={{ height }}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 text-[14px] ${
                selected
                    ? 'border-brand bg-surface-sunken font-semibold text-brand'
                    : 'border-hairline bg-surface text-text-primary'
            } disabled:opacity-40`}
        >
            {children}
        </button>
    );
}

export function NoteSheetHost() {
    const request = useNoteSheet((state) => state.request);
    return request ? <NoteSheet key={request.key ?? 'note'} request={request} /> : null;
}

function NoteSheet({ request }) {
    const close = useNoteSheet((state) => state.close);
    const saved = useNoteSheet((state) => state.saved);
    const noteEdited = useMemoBook((state) => state.noteEdited);
    const editing = request.editing ?? null;
    const fixedMember = request.fixedMember ?? null;
    const [text, setText] = useState(clipNote(editing?.text ?? request.draft ?? ''));
    const [scope, setScope] = useState(
        fixedMember ? `member:${fixedMember}` : (editing?.noteScope ?? request.scope ?? 'all')
    );
    const [kind, setKind] = useState(editing?.kind ?? null);
    const [dueOn, setDueOn] = useState(editing?.dueOn ?? '');
    const [sensitive, setSensitive] = useState(null);
    const [crisis, setCrisis] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!editing) {
            track('note_add_open', { entry: request.entry ?? 'me_tab' });
        }
        requestAnimationFrame(() => {
            const input = inputRef.current;
            if (input) {
                input.focus();
                input.setSelectionRange(input.value.length, input.value.length);
            }
        });
        // 열 때 한 번
    }, []);

    const empty = !text.trim();
    const needsDate = kind === 'schedule' && !dueOn;

    async function submit() {
        setBusy(true);
        setError(null);
        const payload = {
            text: text.trim(),
            scope,
            kind,
            dueOn: kind === 'schedule' ? dueOn : null,
        };
        try {
            if (editing) {
                const result = await meApi.editNote(editing.memoriesId, payload);
                noteEdited(editing.memoriesId, result.memory.text);
                request.onSaved?.(result.memory);
                saved(result.memory);
                return;
            }
            const note = { ...payload, entry: request.entry ?? 'me_tab', confirmSensitive: Boolean(sensitive) };
            // 길게 누르기로 열었으면 그 말(서버가 내 말인지 확인)
            const origin = request.origin ?? null;
            if (origin) {
                note.originMessageId = origin.messageId;
                note.originSurface = origin.surface;
                note.originMemberKey = origin.memberKey ?? undefined;
            }
            const result = await meApi.addNote(note);
            request.onSaved?.(result.note);
            saved(result.note);
        } catch (requestError) {
            if (requestError?.code === 'NOTE_SENSITIVE') {
                setSensitive(requestError.message);
            } else if (requestError?.code === 'CRISIS_NOTE') {
                setCrisis(requestError.details?.safetyLevel ?? 'L2');
            } else {
                setError(errorMessageOf(requestError, '적지 못했어요. 잠시 후 다시 해 주세요.'));
            }
        } finally {
            setBusy(false);
        }
    }

    const fixedName = fixedMember ? getMember(fixedMember)?.name : null;

    return (
        <BottomSheet open onClose={close} label="수첩에 적기">
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                    <Avatar memberKey="imo" size={32} />
                    <h2 className="m-0 text-[17px] leading-6 font-bold">
                        {editing ? '내가 적은 것 고치기' : '수첩에 적어 둘 것'}
                    </h2>
                </div>

                <div className="relative flex flex-col">
                    <textarea
                        ref={inputRef}
                        aria-label="수첩에 적을 말"
                        value={text}
                        placeholder="예: 반려견 이름은 보리"
                        onChange={(event) => {
                            setText(clipNote(event.target.value));
                            // 글이 바뀌면 민감 안내 · 안전 카드는 다시 판단
                            setSensitive(null);
                            setCrisis(null);
                        }}
                        className="min-h-[52px] resize-none rounded-xl border-[1.5px] border-brand bg-bg p-3.5 pb-6 text-[16px] leading-6 outline-none"
                    />
                    <span className="pointer-events-none absolute right-3 bottom-1.5 text-[12px] text-label-muted">
                        60자까지
                    </span>
                </div>

                <div className="flex flex-col gap-2">
                    <span className="text-[13px] font-semibold text-text-secondary">누가 알게 할까요</span>
                    <div role="radiogroup" aria-label="누가 알게 할까요" className="flex flex-wrap gap-2">
                        <ChoiceChip
                            selected={scope === 'all'}
                            disabled={Boolean(fixedMember)}
                            onClick={() => setScope('all')}
                        >
                            모두 (별채)
                        </ChoiceChip>
                        {MEMBER_KEYS.map((memberKey) => (
                            <ChoiceChip
                                key={memberKey}
                                selected={scope === `member:${memberKey}`}
                                disabled={Boolean(fixedMember) && fixedMember !== memberKey}
                                onClick={() => setScope(`member:${memberKey}`)}
                            >
                                <Avatar memberKey={memberKey} size={24} />
                                {getMember(memberKey).name}
                            </ChoiceChip>
                        ))}
                    </div>
                    {fixedName ? (
                        <span className="text-[12px] leading-4 text-text-secondary">
                            이 1:1에서 열어 {fixedName}만 알아요. 다른 멤버는 몰라요.
                        </span>
                    ) : null}
                </div>

                <div className="flex flex-col gap-2">
                    <span className="text-[13px] font-semibold text-text-secondary">어떤 얘기예요 (선택)</span>
                    <div role="radiogroup" aria-label="어떤 얘기예요" className="flex flex-wrap gap-2">
                        {Object.entries(NOTE_KIND_LABELS).map(([key, label]) => (
                            <ChoiceChip
                                key={key}
                                height={32}
                                selected={kind === key}
                                onClick={() => setKind((current) => (current === key ? null : key))}
                            >
                                {label}
                            </ChoiceChip>
                        ))}
                    </div>
                    {kind === 'schedule' ? (
                        <div className="flex flex-col gap-1.5">
                            <input
                                type="date"
                                aria-label="일정 날짜"
                                value={dueOn}
                                min={todayInSeoul()}
                                onChange={(event) => setDueOn(event.target.value)}
                                className="h-11 rounded-xl border border-hairline bg-surface px-3 text-[15px]"
                            />
                            <span className="text-[12px] leading-4 text-text-secondary">
                                그날 멤버가 먼저 물어봐요.
                            </span>
                        </div>
                    ) : null}
                </div>

                {sensitive ? (
                    <p
                        role="status"
                        className="m-0 rounded-xl bg-[#F7E3D2] px-3.5 py-3 text-[14px] leading-5 text-[#8F4A14]"
                    >
                        {sensitive}
                    </p>
                ) : null}
                {crisis ? <SafetyCard level={crisis} /> : null}
                {error ? (
                    <p role="alert" className="m-0 text-caption text-error">
                        {error}
                    </p>
                ) : null}

                <Button size="lg" disabled={empty || needsDate || Boolean(crisis)} busy={busy} onClick={submit}>
                    {editing ? '고친 대로 적기' : '수첩에 적기'}
                </Button>
            </div>
        </BottomSheet>
    );
}
